import { useState } from "react";
import type { AccountPlatform, OrderResponse } from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { CountdownTimer } from "./CountdownTimer";
import { describeError } from "./States";

/**
 * Buyer-facing guidance + credential reveal for an ACCOUNT_TRANSFER order. No platform we support
 * exposes an API that can safely automate the actual handoff (see
 * apps/api/src/services/accountTransfers.ts), so this is mostly about setting honest expectations
 * at each stage rather than driving an automated flow — the one real action is revealing the
 * final credentials once an admin has verified and submitted them.
 */

/** YOUTUBE's note names the real intermediary Google account when the platform has one
 *  configured (YOUTUBE_TRANSFER_GOOGLE_ACCOUNT) — falling back to generic phrasing otherwise, so
 *  a deployment that hasn't set one up yet still renders sensible copy instead of a broken
 *  template. */
function platformHandoffNote(platform: AccountPlatform, youtubeAccount?: string): string {
  if (platform === "YOUTUBE") {
    const accountPhrase = youtubeAccount
      ? `our Google account (${youtubeAccount})`
      : "our Google account";
    return (
      "For a YouTube channel, the handoff goes through Google's own Brand Account ownership " +
      "transfer, including a mandatory 7-day wait Google enforces before an added owner can " +
      "become the channel's Primary Owner — that's why this order's dispute window is longer " +
      `than usual. We act as a neutral intermediary for this step: the seller invites ${accountPhrase} ` +
      "as Owner, the same way your payment sits in the smart contract rather than going straight to the " +
      "seller. If you haven't already, submit the Google account email you want added below — " +
      "the sooner the seller has it, the sooner your 7-day wait can start."
    );
  }
  return PLATFORM_HANDOFF_NOTE[platform];
}

const PLATFORM_HANDOFF_NOTE: Record<Exclude<AccountPlatform, "YOUTUBE">, string> = {
  X_TWITTER:
    "X has no built-in way to transfer full ownership, so the seller hands the account to a " +
    "login only we control first — that way nobody can quietly keep access and revoke yours " +
    "later. We verify the account, then reset it again and hand you the final login.",
  TIKTOK:
    "TikTok has no official transfer process for personal accounts, so the seller hands the " +
    "account to a login only we control first, the same way we handle X — we verify it, then " +
    "reset it again and hand you the final login.",
  INSTAGRAM:
    "The seller hands the account to a login only we control first so we can verify it before " +
    "resetting it again and handing you the final login.",
  DISCORD:
    "The seller hands the account/server to a login only we control first so we can verify it " +
    "before resetting it again and handing you final access.",
  OTHER:
    "The seller hands the account to a login only we control first so we can verify it before " +
    "resetting it again and handing you the final login.",
};

const STAGE_COPY: Record<string, { title: string; detail: string }> = {
  AWAITING_SELLER_HANDOFF: {
    title: "Waiting on the seller",
    detail: "The seller needs to hand the account off to MarketOnBase before anything else can happen.",
  },
  PLATFORM_VERIFYING: {
    title: "MarketOnBase is verifying the account",
    detail: "We've received the account from the seller and are confirming it matches this listing.",
  },
  READY_FOR_BUYER: {
    title: "Your account is ready",
    detail: "Reveal your final login details below, then verify you have full access before releasing funds.",
  },
  CREDENTIALS_REVEALED: {
    title: "You've claimed your credentials",
    detail: "Make sure you have full, sole control of the account, then release funds below.",
  },
};

/** The seller's half of the handoff — submitting this hands the account to MarketOnBase's
 *  custody and auto-advances the order to PLATFORM_VERIFYING. Its own component so draft
 *  state doesn't get wiped by the order refetch this triggers. */
function SellerHandoffForm({
  orderId,
  accountPlatform,
  alreadySubmitted,
  onSubmitted,
  youtubeAccount,
}: {
  orderId: string;
  accountPlatform: AccountPlatform | null;
  alreadySubmitted: boolean;
  onSubmitted: () => void;
  youtubeAccount?: string;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isYouTube = accountPlatform === "YOUTUBE";
  const isDone = justSubmitted || alreadySubmitted;

  async function handleSubmit() {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await apiClient.submitAccountTransferSellerHandoff(orderId, {
        username: username.trim() || undefined,
        password: password || undefined,
        notes: notes.trim() || undefined,
      });
      setJustSubmitted(true);
      setIsEditing(false);
      onSubmitted();
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isDone && !isEditing) {
    return (
      <div className="stack">
        <p className="text-sm muted">
          {isYouTube
            ? "Thanks — once you've added our Google account as Owner, we'll take it from here."
            : "Submitted — MarketOnBase is verifying the account."}
        </p>
        <div className="row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}>
            Made a mistake? Submit a correction
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <p className="text-sm muted">
        {isYouTube
          ? `Once you've added ${youtubeAccount ?? "our Google account"} as Owner on the ` +
            "channel's Brand Account, confirm it here so we know to start verifying — we'll add " +
            "the buyer as an owner too right away so their 7-day wait starts the same day as " +
            "ours, not after."
          : "Reset the account's login to something only you know, then submit it below so " +
            "MarketOnBase can take custody and verify it."}
      </p>
      <div className="row" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
          <label htmlFor="seller-handoff-username">Username / email</label>
          <input
            id="seller-handoff-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={200}
          />
        </div>
        {!isYouTube && (
          <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
            <label htmlFor="seller-handoff-password">Password</label>
            <input
              id="seller-handoff-password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={500}
            />
          </div>
        )}
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="seller-handoff-notes">Notes</label>
        <textarea
          id="seller-handoff-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={4000}
          placeholder={
            isYouTube
              ? `e.g. Added ${youtubeAccount ?? "our Google account"} as Owner on the Brand ` +
                "Account on Sep 4."
              : "Anything the platform should know before verifying (2FA method, recovery email, etc.)"
          }
        />
      </div>
      {submitError && (
        <p className="field-error-text" role="alert">
          {submitError}
        </p>
      )}
      <div className="row">
        <button
          type="button"
          className="btn btn-primary"
          disabled={isSubmitting || (!username.trim() && !password && !notes.trim())}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? "Submitting…" : "Hand off account to MarketOnBase"}
        </button>
      </div>
    </div>
  );
}

/** YOUTUBE-only: the buyer submits the Google account email to add as a Brand Account owner, as
 *  early as possible — the seller can invite it the same day they invite the platform, so the
 *  buyer's own 7-day Owner-since clock starts on day 0 instead of days later. */
function BuyerGoogleEmailForm({
  orderId,
  alreadySubmittedEmail,
  onSubmitted,
}: {
  orderId: string;
  alreadySubmittedEmail: string | null;
  onSubmitted: () => void;
}) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  async function handleSubmit() {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await apiClient.submitAccountTransferBuyerEmail(orderId, { email: email.trim() });
      setIsEditing(false);
      onSubmitted();
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (alreadySubmittedEmail && !isEditing) {
    return (
      <div className="stack">
        <div className="row row--between">
          <span className="muted text-sm">Google account submitted</span>
          <span className="mono">{alreadySubmittedEmail}</span>
        </div>
        <div className="row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}>
            Wrong email? Correct it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="buyer-google-email">Your Google account email</label>
        <input
          id="buyer-google-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={320}
          placeholder="you@gmail.com"
        />
        <span className="hint">
          The Google account you want added as an owner of the channel — not necessarily the email
          you signed in with here.
        </span>
      </div>
      {submitError && (
        <p className="field-error-text" role="alert">
          {submitError}
        </p>
      )}
      <div className="row">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={isSubmitting || !email.trim()}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? "Submitting…" : "Submit Google account"}
        </button>
      </div>
    </div>
  );
}

export function AccountTransferPanel({
  order,
  isBuyer,
  isSeller,
  onOrderChanged,
}: {
  order: OrderResponse;
  isBuyer: boolean;
  isSeller: boolean;
  onOrderChanged: () => void;
}) {
  const [revealed, setRevealed] = useState<{
    username: string | null;
    password: string | null;
    notes: string | null;
  } | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const { data: publicConfig } = usePublicConfig();

  const stage = order.accountTransferStage;
  if (!stage) return null;
  const copy = STAGE_COPY[stage] ?? STAGE_COPY.AWAITING_SELLER_HANDOFF;

  // The buyer's own 7-day Google-enforced wait to become Primary Owner starts the day the seller
  // invites both us and the buyer as Owner — which is this order's sellerHandoffSubmittedAt (our
  // own submission timestamp, not something Google confirms back to us, so this is our best
  // estimate rather than a verified fact — still far more useful to a waiting buyer than no
  // countdown at all). Moot once credentials are already revealed.
  const youtubeOwnerWaitDeadline =
    isBuyer && order.accountPlatform === "YOUTUBE" && order.sellerHandoffSubmittedAt && stage !== "CREDENTIALS_REVEALED"
      ? new Date(
          new Date(order.sellerHandoffSubmittedAt).getTime() + 7 * 24 * 60 * 60 * 1000,
        ).toISOString()
      : null;

  async function handleReveal() {
    setRevealError(null);
    setIsRevealing(true);
    try {
      const result = await apiClient.revealAccountTransferCredentials(order.id);
      setRevealed(result);
    } catch (err) {
      setRevealError(describeError(err));
    } finally {
      setIsRevealing(false);
    }
  }

  const canReveal =
    isBuyer && (stage === "READY_FOR_BUYER" || stage === "CREDENTIALS_REVEALED") && !revealed;
  const canSubmitHandoff =
    isSeller && (stage === "AWAITING_SELLER_HANDOFF" || stage === "PLATFORM_VERIFYING");
  const canSubmitBuyerEmail =
    isBuyer &&
    order.accountPlatform === "YOUTUBE" &&
    (stage === "AWAITING_SELLER_HANDOFF" || stage === "PLATFORM_VERIFYING");

  return (
    <div className="card stack">
      <h2>Account handoff</h2>
      <div className="alert alert--info" role="status">
        <strong>{copy?.title}</strong>
        <p>{copy?.detail}</p>
      </div>

      {youtubeOwnerWaitDeadline && (
        <p className="text-sm muted row" style={{ gap: "0.5rem", alignItems: "center" }}>
          Your 7-day wait to become Primary Owner: <CountdownTimer deadlineIso={youtubeOwnerWaitDeadline} />
          <span className="text-sm muted">
            (estimated from when the seller confirmed the handoff — Google doesn't report this to
            us directly)
          </span>
        </p>
      )}

      {stage === "AWAITING_SELLER_HANDOFF" && order.accountPlatform && (
        <p className="text-sm muted">
          {platformHandoffNote(order.accountPlatform, publicConfig?.youtubeTransferGoogleAccount)}
        </p>
      )}

      {canSubmitBuyerEmail && (
        <BuyerGoogleEmailForm
          orderId={order.id}
          alreadySubmittedEmail={order.buyerGoogleEmail}
          onSubmitted={onOrderChanged}
        />
      )}

      {canSubmitHandoff && (
        <SellerHandoffForm
          orderId={order.id}
          accountPlatform={order.accountPlatform}
          alreadySubmitted={order.sellerHandoffSubmitted}
          onSubmitted={onOrderChanged}
          youtubeAccount={publicConfig?.youtubeTransferGoogleAccount}
        />
      )}

      {canReveal && (
        <button
          type="button"
          className="btn btn-primary"
          disabled={isRevealing}
          onClick={() => void handleReveal()}
        >
          {isRevealing ? "Revealing…" : "Reveal my login details"}
        </button>
      )}

      {revealError && (
        <p className="field-error-text" role="alert">
          {revealError}
        </p>
      )}

      {revealed && (
        <div className="card stack success-pop">
          <span className="row" style={{ gap: "0.5rem" }}>
            <span className="success-check" aria-hidden="true">
              ✓
            </span>
            <span className="muted text-sm">Your account credentials</span>
          </span>
          {revealed.username && (
            <div className="row row--between">
              <span className="muted text-sm">Username / email</span>
              <span className="mono">{revealed.username}</span>
            </div>
          )}
          {revealed.password && (
            <div className="row row--between">
              <span className="muted text-sm">Password</span>
              <span className="mono">{revealed.password}</span>
            </div>
          )}
          {revealed.notes && (
            <div className="stack" style={{ gap: "var(--space-1)" }}>
              <span className="muted text-sm">Notes</span>
              <p className="text-sm" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {revealed.notes}
              </p>
            </div>
          )}
          <p className="text-sm muted">
            Change the password, email, and any linked 2FA before you rely on this account being
            yours alone — then release funds once you've confirmed full access.
          </p>
        </div>
      )}
    </div>
  );
}
